/**
 * Plugin Development Studio
 *
 * This is the main component for the plugin development environment.
 *
 */
import React, { useEffect } from "react"
import { useParams } from "react-router-dom"

import { EFeatureFlags } from "@letshang/constants"
import { useProcessFeatureFlags } from "src/settings/featureFlags"

// Import Recoil hooks
import { usePlugins } from "../../../recoil/pluginStudio"
// Import UI components
import CodeEditorPanel from "../components/CodeEditorPanel"
import FileBreadcrumbs from "../components/FileBreadcrumbs"
import FileExplorer from "../components/FileExplorer"
import FloatingToolbar from "../components/FloatingToolbar"
import HeaderPanel from "../components/HeaderPanel"
import LiveDataPanel from "../components/LiveDataPanel"
import PreviewPanel from "../components/PreviewPanel"
import StudioLayout from "../components/StudioLayout"
import TestDataPanel from "../components/TestDataPanel"

/**
 * Main plugin development studio component
 * Acts as a container for the specialized components that manage their own state
 */
const PluginDevelopmentStudio: React.FC = () => {
  console.log("[PluginStudio] Rendering")

  const isPluginCreationEnabled = useProcessFeatureFlags(
    EFeatureFlags.ENABLE_PLUGIN_CREATION,
  )

  const { plugin_id: urlPluginId } = useParams()

  // We only need minimal hook usage here for URL-based plugin selection
  const { plugins, selectPlugin } = usePlugins()

  // Select plugin from URL if available
  useEffect(() => {
    if (urlPluginId && plugins.some((p) => p.id === urlPluginId)) {
      selectPlugin(urlPluginId)
    }
  }, [urlPluginId, plugins, selectPlugin])

  // If plugin creation is not enabled, show permission denied message
  if (!isPluginCreationEnabled) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white p-8 max-w-md">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-4">Access Restricted</h2>
          <p className="text-gray-300 mb-6">
            You don't have permission to access the Plugin Studio. Plugin
            creation is currently disabled.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <StudioLayout
        fileExplorer={<FileExplorer />}
        header={<HeaderPanel />}
        codeEditor={<CodeEditorPanel />}
        testDataPanel={<TestDataPanel />}
        liveDataPanel={<LiveDataPanel />}
        previewPanel={<PreviewPanel />}
        breadcrumbs={<FileBreadcrumbs />}
      />
      <FloatingToolbar />
    </>
  )
}

export default PluginDevelopmentStudio
